import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { BackButton } from '../components/back-button'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@dogdisk/shared'
import { MobileScreen } from '../components/mobile-screen'
import { CoverPicker } from '../components/modals/cover-picker'
import { MediaArea } from '../components/file-view/media-area'
import { DetailsCard, type FileViewKind } from '../components/file-view/details-card'
import { useFileMeta } from '../components/file-view/use-file-meta'
import { resourceUrl } from '../_lib/resource-node-pref'

// 详情页 /file/:fileId(自己的文件,🔒 Authed)— .pen 帧:
//   image → zkY66 / video → kENKt / audio → qCOtS / 其他 → pKcdM
// 与预览页(/preview)区别(D1 按入口分流):
//   - 无社交(喜欢/不喜欢)— 自己的文件不展示点赞数,也无转存
//   - 无标签 section(展示与编辑均已移除)
//   - 视频 + 音频显示 CoverRow(→ NxbQB 选择封面)
//   - Header moreBtn(owner 动作,.pen → BQOCz);底部仅「下载」(已无单文件「分享」按钮)

export function FilePreviewPage() {
  const { fileId } = useParams<{ fileId: string }>()
  const qc = useQueryClient()
  const [coverOpen, setCoverOpen] = useState(false)
  const [downloadError, setDownloadError] = useState<string | null>(null)

  const fm = useFileMeta(fileId)

  const kind: FileViewKind = fm.isImage ? 'image' : fm.isVideo ? 'video' : fm.isAudio ? 'audio' : 'other'
  const frameId = fm.isImage ? 'zkY66' : fm.isVideo ? 'kENKt' : fm.isAudio ? 'qCOtS' : 'pKcdM'
  const showCoverRow = fm.isVideo || fm.isAudio

  const setCoverMut = useMutation({
    // 封面写入收口到 gapi(service-role,绕过拦 pack_file 的 RLS);详情页设封面与 useSetCover 一致。
    mutationFn: async (newCoverId: string | null) => {
      await apiClient.post(
        `/gapi/user/files/${encodeURIComponent(fileId!)}/cover`,
        newCoverId ? { cover_file_info_id: newCoverId } : { clear: true },
      )
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['file', 'meta', fileId] }),
  })

  const downloadMut = useMutation({
    mutationFn: async () => {
      if (!fileId) throw new Error('no fileId')
      return apiClient.get<{ url: string; expires_at: string }>(`/gapi/user/download/${fileId}`)
    },
    onSuccess: (resp) => {
      if (!resp.url) {
        setDownloadError('下载链接为空')
        return
      }
      window.location.href = resourceUrl(resp.url)
    },
    onError: (e) => setDownloadError(e instanceof Error ? e.message : '下载失败'),
  })

  return (
    <MobileScreen bg="white">
      {/* Header(HJWZI):44×44 surface 圆角方钮 + 22px 标题(buttons fill $surface-color) */}
      <header
        data-testid={frameId}
        className="flex items-center justify-between gap-3 px-6 pt-[max(10px,env(safe-area-inset-top))] pb-3"
      >
        <BackButton className="shrink-0" />
        <h1 className="flex-1 truncate text-center text-[22px] font-semibold text-[#1A1A1A]">{fm.meta.data?.name ?? '文件详情'}</h1>
        <button
          type="button"
          data-testid={`${frameId}-more-btn`}
          className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[#F6F7F8] text-[#1A1A1A] active:bg-[#EDEEF0]"
          aria-label="更多"
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>
        </button>
      </header>

      {/* grow={false}:详情页媒体区不撑满视口,内容自上而下排布,封面/信息卡贴近媒体(见下方 mt-[34px]) */}
      <MediaArea
        grow={false}
        frameId={frameId}
        isImage={fm.isImage}
        isVideo={fm.isVideo}
        isAudio={fm.isAudio}
        isLoading={fm.meta.isLoading}
        streamURL={fm.streamURL}
        coverUrl={fm.coverUrl.data ?? null}
        name={fm.meta.data?.name}
        mime={fm.mime}
      />

      {/* CoverRow(zYxFk):surface 行卡 + 图标(无独立色块)。视频 + 音频(点击 → NxbQB 选择封面)。
          mt-[34px]:与上方媒体区固定间距(媒体下第一个卡片) */}
      {fm.meta.data && showCoverRow && (
        <button
          type="button"
          data-testid={`${frameId}-cover-row`}
          onClick={() => setCoverOpen(true)}
          className="mx-5 mb-4 mt-[34px] flex items-center gap-3 rounded-2xl bg-[#F6F7F8] px-4 py-3 text-left active:bg-[#EDEEF0]"
        >
          <span className="flex items-center text-[#1A1A1A]">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-5-5L5 21" /></svg>
          </span>
          <span className="flex-1">
            <span className="block text-[14px] font-medium text-[#1A1A1A]">{fm.isVideo ? '视频封面' : '音频封面'}</span>
            <span className="block text-[11px] text-[#9CA3AF]">
              {fm.meta.data.cover_file_info_id ? '已设置' : '未设置 · 列表显示占位'}
            </span>
          </span>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="2"><path d="m9 18 6-6-6-6" /></svg>
        </button>
      )}

      {/* 信息卡。无 CoverRow 时(图片 / 非媒体)信息卡是媒体下第一个卡片 → mt-[34px] 固定间距 */}
      {fm.meta.data && (
        <div className={`px-5 pb-2 ${showCoverRow ? '' : 'mt-[34px]'}`}>
          <DetailsCard
            frameId={frameId}
            kind={kind}
            mime={fm.mime}
            size={fm.meta.data.size}
            createdAt={fm.meta.data.created_at}
            imageInfo={fm.imageInfo.data ?? null}
            videoInfo={fm.videoInfo.data ?? null}
            audioInfo={fm.audioInfo.data ?? null}
          />
        </div>
      )}

      {/* 弹性占位:内容不足时把下载条压到屏幕底部 */}
      <div className="flex-1" />

      {/* 下载条悬浮固定屏幕底部:sticky bottom-0;周边背景透明(仅按钮自身有底色),不随页面大小上下浮动 */}
      <div
        className="sticky bottom-0 z-10 bg-transparent px-5 pt-2"
        style={{ paddingBottom: 'max(34px, env(safe-area-inset-bottom))' }}
      >
        {downloadError && <p className="mb-2 text-center text-[12px] text-[#FF6B6B]">{downloadError}</p>}
        {/* 仅「下载」— 单文件「分享」已移除(分享=资源包,见 spec D3) */}
        <button
          data-testid={`${frameId}-download-btn`}
          onClick={() => {
            setDownloadError(null)
            downloadMut.mutate()
          }}
          disabled={downloadMut.isPending}
          className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#19caad] text-[15px] font-semibold text-white disabled:opacity-50"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M12 4v12M5 11l7 7 7-7" /></svg>
          下载
        </button>
      </div>

      {/* NxbQB 选择封面(视频/音频封面行触发) */}
      <CoverPicker
        open={coverOpen}
        onClose={() => setCoverOpen(false)}
        currentCoverId={fm.meta.data?.cover_file_info_id ?? null}
        defaultFolderId={fm.meta.data?.parent_id ?? null}
        onSelect={(pick) => setCoverMut.mutate(pick.id)}
        onClear={() => setCoverMut.mutate(null)}
      />
    </MobileScreen>
  )
}
